import React from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons/faTriangleExclamation';
import { faCircleInfo } from '@fortawesome/pro-light-svg-icons/faCircleInfo';
import type { SerializedMediaWidgetData } from 'fym-shared/src/smartlinks/mediaWidget';
import { TypedMemo } from '../../../../../common/types';
import { Panel } from '../../../../../music24/components/primitives/Panel';
import { SMLButton } from '../../../../../music24/components/primitives/SMLButton';
import { mediaBlockMessages } from '../mediaBlockMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Media block editor; each part uses its own slice of it */
import styles from './MediaWidgetEditionView.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface MediaBlockProblemBoxProps {
  data: SerializedMediaWidgetData;
  onPickAnother: () => void;
  onDelete: () => void;
}

/**
 * Why the page does not show the block, or shows an older revision (design d1, d3). A gone file or
 * song gives "Pick another file" and "Delete block". A file that renders gives a note only.
 */
const MediaBlockProblemBoxComponent: React.FC<MediaBlockProblemBoxProps> = ({ data, onPickAnother, onDelete }) => {
  const intl = useIntl();
  const { problem, renderingRevision, media } = data;

  if (media !== null && renderingRevision !== null) {
    const message =
      renderingRevision.revision === null || media.revision === null
        ? intl.formatMessage(mediaBlockMessages.newerRenderingNoNumber, { progress: renderingRevision.progress })
        : intl.formatMessage(mediaBlockMessages.newerRendering, {
            next: renderingRevision.revision,
            current: media.revision,
            progress: renderingRevision.progress,
          });
    return (
      <p className={styles.infoLine} data-testid="media-block-rendering">
        <FontAwesomeIcon icon={faCircleInfo} /> {message}
      </p>
    );
  }
  if (media !== null || problem === null) {
    return null;
  }
  if (problem === 'rendering') {
    return (
      <p className={styles.infoLine} data-testid="media-block-problem">
        <FontAwesomeIcon icon={faCircleInfo} /> {intl.formatMessage(mediaBlockMessages.renderingBody)}
      </p>
    );
  }

  return (
    <Panel nested tone="danger" className={styles.problemBox} data-testid="media-block-problem">
      <p className={styles.problemText}>
        <FontAwesomeIcon icon={faTriangleExclamation} />{' '}
        {intl.formatMessage(problem === 'songGone' ? mediaBlockMessages.songGoneBody : mediaBlockMessages.fileGoneBody)}
      </p>
      <div className={styles.problemActions}>
        <SMLButton variant="primary" onClick={onPickAnother}>
          {intl.formatMessage(mediaBlockMessages.pickAnother)}
        </SMLButton>
        <SMLButton onClick={onDelete}>{intl.formatMessage(mediaBlockMessages.deleteBlock)}</SMLButton>
      </div>
    </Panel>
  );
};

export const MediaBlockProblemBox = TypedMemo(MediaBlockProblemBoxComponent);
